import React, {AnchorHTMLAttributes, ReactNode} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {Size} from 'types/Theme'

import {cssTheme} from '../../../themes'
import {linariaMq} from '../../../utils/breakpoints'

export type ButtonLinkSize = Extract<Size, 'sm' | 'md' | 'lg'>
export const BUTTON_LINK_VARIANTS = ['primary', 'secondary', 'inverse'] as const
export type ButtonLinkVariant = (typeof BUTTON_LINK_VARIANTS)[number]

interface Props extends AnchorHTMLAttributes<HTMLAnchorElement> {
  /** Pass through className to allow styles overrides */
  className?: string
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string
  /** Size variant. If no size is provided, font-size inherited from surrounding text. */
  size?: ButtonLinkSize
  /** Whether the ButtonLink should have a Default, Info, Strong or Inverse variant. */
  variant?: ButtonLinkVariant
  /** An Icon to display on the left side of the Link for LTR languages. Inverted on RTL */
  iconStart?: ReactNode
  /** An Icon to display on the right side of the Link for LTR languages. Inverted on RTL */
  iconEnd?: ReactNode
  /** Whether the link should be compact or not */
  isCompact?: boolean
  /** Whether the font-weight is bold or not */
  isBold?: boolean
  /** Whether the underline is dotted or not */
  isDotted?: boolean
  style?: React.CSSProperties
  /** Render as a span, e.g. when nested inside another link */
  tag?: 'span'
}

const textSizeMap = {
  sm: cssTheme.typography.bodyXS,
  md: cssTheme.typography.bodyS,
  lg: cssTheme.typography.bodyM
} as const

const boldTextSizeMap = {
  sm: cssTheme.typography.labelXS,
  md: cssTheme.typography.labelS,
  lg: cssTheme.typography.labelM
} as const

export const ButtonLinkMinHeightMap: Record<ButtonLinkSize, number> = {
  sm: 21,
  md: 26,
  lg: 30
} as const

export const ButtonLinkSizesMap: Record<ButtonLinkSize, number> = {
  sm: 26,
  md: 34,
  lg: 41
} as const

const getSizeStyles = (size: ButtonLinkSize) => `
  &.--size-${size} {
    min-height: ${ButtonLinkSizesMap[size]}px;
    min-width: ${ButtonLinkSizesMap[size]}px;
    &.--compact {
      min-height: ${ButtonLinkMinHeightMap[size]}px;
      min-width: auto;
    }
  }
`

const getVariantStyles = (variant: ButtonLinkVariant) => `
  &.--variant-${variant} {
    color: ${cssTheme.colors.buttonLink[variant].default.content};
    &:visited {
      color: ${cssTheme.colors.buttonLink[variant].default.content};
    }

    ${linariaMq.desktopXs} {
      &:hover,
      &:focus {
        color: ${cssTheme.colors.buttonLink[variant].hover.content};
        background: ${cssTheme.colors.buttonLink[variant].hover.background};
        text-decoration-line: underline;
      }
    }
  }
`
const ButtonLinkWrapper = styled.a`
  display: inline-flex;
  align-items: center;
  font-size: inherit;
  line-height: inherit;
  border-radius: ${cssTheme.layout.radius.sm};
  text-decoration: underline;
  text-underline-offset: 3px;
  padding: 0 ${cssTheme.layout.spacing.s300};

  ${Object.keys(ButtonLinkSizesMap)
    .map(size => getSizeStyles(size as ButtonLinkSize))
    .join('')}

  ${BUTTON_LINK_VARIANTS.map(variant => getVariantStyles(variant)).join('')}

  &.--compact {
    padding: ${cssTheme.layout.spacing.s100} 0;
  }

  &.--dotted {
    text-decoration-style: dotted;

    ${linariaMq.desktopXs} {
      &:hover,
      &:focus {
        text-decoration-style: dotted;
      }
    }
    &:active {
      text-decoration-style: dotted;
    }
  }
`

const TextWrapper = styled.span`
  user-select: none;
  &.--size-sm {
    ${textSizeMap.sm}
    &.--is-bold {
      ${boldTextSizeMap.sm}
    }
  }
  &.--size-md {
    ${textSizeMap.md}
    &.--is-bold {
      ${boldTextSizeMap.md}
    }
  }
  &.--size-lg {
    ${textSizeMap.lg}
    &.--is-bold {
      ${boldTextSizeMap.lg}
    }
  }
  ${linariaMq.desktopXs} {
    user-select: auto;
  }
`

export const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  &:first-of-type {
    margin-right: ${cssTheme.layout.spacing.s100};
  }
  &:last-of-type {
    margin-left: ${cssTheme.layout.spacing.s100};
  }
`

export const ButtonLink = ({
  children,
  size = 'md',
  variant = 'primary',
  isCompact = false,
  dataId = '',
  iconEnd,
  iconStart,
  className,
  isBold = true,
  isDotted = false,
  style,
  tag,
  ...anchorProps
}: Props) => (
  <ButtonLinkWrapper
    as={tag}
    data-id={dataId}
    className={cx(
      `--size-${size}`,
      `--variant-${variant}`,
      isCompact && '--compact',
      isDotted && '--dotted',
      className
    )}
    style={style}
    {...anchorProps}
  >
    {iconStart && <IconWrapper>{iconStart}</IconWrapper>}
    <TextWrapper className={cx(`--size-${size}`, isBold && '--is-bold')}>
      {children}
    </TextWrapper>
    {iconEnd && <IconWrapper>{iconEnd}</IconWrapper>}
  </ButtonLinkWrapper>
)
